/**
 * Drawer UI Web Component
 * Handles opening/closing of drawer overlays with proper accessibility
 */
class DrawerUI extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.trigger = null;
    this.bindEvents();
  }

  connectedCallback() {
    // Find the trigger button that controls this drawer
    const drawerId = this.getAttribute('id');
    this.trigger = document.querySelector(`[aria-controls="${drawerId}"]`);
    
    if (this.trigger) {
      this.trigger.addEventListener('click', this.toggle.bind(this));
    }
    
    // Handle escape key
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));
    
    // Handle clicks on the drawer itself (backdrop clicks)
    this.addEventListener('click', this.handleBackdropClick.bind(this));
  }

  bindEvents() {
    // Listen for action buttons using data-action attribute
    this.addEventListener('click', (e) => {
      const action = e.target.getAttribute('data-action') || e.target.closest('[data-action]')?.getAttribute('data-action');
      
      if (action === 'close-drawer') {
        this.close();
      } else if (action === 'toggle-drawer') {
        this.toggle();
      }
    });
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    document.documentElement.classList.add('drawer-open');
    this.classList.add('drawer--open');
    document.querySelector('#chat-button').style.opacity = '0';
    
    // Update trigger button state
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    // Focus management
    this.setAttribute('tabindex', '-1');
    this.focus();

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('drawer:opened', {
      bubbles: true,
      detail: { drawer: this }
    }));
  }

  close() {
    this.isOpen = false;
    document.documentElement.classList.remove('drawer-open');
    this.classList.remove('drawer--open');
    document.querySelector('#chat-button').style.opacity = '1';

    // Update trigger button state
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.focus(); // Return focus to trigger
    }

    // Dispatch close event
    this.dispatchEvent(new CustomEvent('drawer:closed', {
      bubbles: true,
      detail: { drawer: this }
    }));
  }

  handleBackdropClick(e) {
    // Close drawer if clicking on the backdrop (drawer itself, not its contents)
    // The backdrop is the drawer element itself, content is inside drawer__content
    if (e.target === this && this.isOpen) {
      this.close();
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }
}

// Register the custom element
if (!customElements.get('drawer-ui')) {
  customElements.define('drawer-ui', DrawerUI);
}
